import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight, Camera, Film, Package, Play } from "lucide-react";
import axios from "axios";
import { resolveImageUrl } from "../utils/image";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HERO_VIDEO_ID = "13Ezdthl6Ko";

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

const DEFAULT_ABOUT_IMAGE = "https://images.unsplash.com/photo-1765344550212-a3b963d63c32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjB2aWRlbyUyMHByb2R1Y3Rpb24lMjBiZWhpbmQlMjB0aGUlMjBzY2VuZXMlMjBjYW1lcmElMjBjcmV3fGVufDB8fHx8MTc3MTYyMDgyMHww&ixlib=rb-4.1.0&q=85";

const DEFAULTS = {
  hero_tagline: "Premium Visual Content",
  hero_headline: "We Create Visuals That Move Brands Forward.",
  hero_description: "Strategic photography and video for modern businesses. Cinematic, purposeful, results-driven.",
  hero_cta_primary: "View Our Work",
  hero_cta_secondary: "Book a Consultation",
  about_label: "About OBRA",
  about_title: "More Than Content. We Build Brand Presence.",
  about_p1: "OBRA Creatives is the commercial division of Spektrum Visuals. We specialize in delivering powerful visual content that elevates brands and drives measurable results.",
  about_p2: "Our approach combines strategic thinking with cinematic execution. Every frame, every shot, every edit is designed with your business objectives in mind.",
};

const services = [
  {
    icon: Camera,
    title: "Commercial Photography",
    description: "Strategic brand imagery that captures attention and drives results. From product shots to corporate portraits.",
    link: "/services#photography"
  },
  {
    icon: Film,
    title: "Commercial Videography",
    description: "Cinematic brand films and promotional content that tells your story with impact and purpose.",
    link: "/services#videography"
  },
  {
    icon: Package,
    title: "Social Media Content",
    description: "Photo and video content for social media, including Reels, regular content shoots, and monthly plans.",
    link: "/services#packages"
  }
];

// ── Staggered word reveal ──
function AnimatedHeadline({ text, className, delay = 0 }) {
  const words = text.split(" ");
  return (
    <motion.h1 className={className}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden mr-[0.3em]">
          <motion.span
            className="inline-block"
            initial={{ y: "110%", rotateX: -80 }}
            animate={{ y: 0, rotateX: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * 0.06,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </motion.h1>
  );
}

// ── Counting number animation ──
function CountUp({ target, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const num = parseInt(target.replace(/\D/g, ""));

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = Math.max(1, Math.floor(num / (duration * 60)));
    const timer = setInterval(() => {
      start += step;
      if (start >= num) { setCount(num); clearInterval(timer); }
      else setCount(start);
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, num, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// ── Parallax image ──
function ParallaxImage({ src, alt, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div ref={ref} className={`overflow-hidden ${className || ""}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-[116%] object-cover"
      />
    </div>
  );
}

// ── Scroll reveal wrapper ──
function Reveal({ children, delay = 0, direction = "up" }) {
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 40 : direction === "down" ? -40 : 0,
      x: direction === "left" ? 40 : direction === "right" ? -40 : 0,
    },
    visible: { opacity: 1, y: 0, x: 0 },
  };
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={variants}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}


export default function Home() {
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [aboutImage, setAboutImage] = useState(DEFAULT_ABOUT_IMAGE);
  const [heroVideoId, setHeroVideoId] = useState(DEFAULT_HERO_VIDEO_ID);
  const [heroVideoFile, setHeroVideoFile] = useState(null);
  const [content, setContent] = useState({});

  const c = (key) => content[key] || DEFAULTS[key] || "";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [portfolioRes, testimonialsRes, settingsRes, contentRes] = await Promise.all([
        axios.get(`${API}/portfolio?featured=true`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/site-content`)
      ]);
      setPortfolio(portfolioRes.data.slice(0, 4));
      setTestimonials(testimonialsRes.data);
      if (settingsRes.data.home_about_image) setAboutImage(settingsRes.data.home_about_image);
      if (settingsRes.data.hero_video_file) {
        setHeroVideoFile(`${BACKEND_URL}${settingsRes.data.hero_video_file}`);
      } else if (settingsRes.data.hero_video_url) {
        const vid = extractYouTubeId(settingsRes.data.hero_video_url);
        if (vid) setHeroVideoId(vid);
      }
      setContent(contentRes.data || {});
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div data-testid="home-page">
      <SEO path="/" />

      {/* ═══ Hero Section ═══ */}
      <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-black">
        {/* Video background — native MP4 or YouTube fallback */}
        <div className="absolute inset-0 z-10">
          <div className="absolute inset-0 overflow-hidden">
            {heroVideoFile ? (
              <video
                key={heroVideoFile}
                autoPlay muted loop playsInline preload="metadata"
                src={heroVideoFile}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover"
                data-testid="hero-video-native"
                onError={(e) => {
                  console.warn("Hero video failed to load, falling back to YouTube");
                  setHeroVideoFile(null);
                }}
              />
            ) : (
              <iframe
                src={`https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&vq=hd1080&hd=1`}
                title="OBRA Creatives Hero Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ width: '100vw', height: '56.25vw', minHeight: '100vh', minWidth: '177.77vh' }}
              />
            )}
          </div>
        </div>
        {/* Overlay — transparent at top, fades to solid at bottom */}
        <div className="absolute inset-0 z-20 hero-overlay" />

        <div className="relative z-30 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <div className="max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                  {c("hero_tagline")}
                </span>
              </motion.div>

              <AnimatedHeadline
                text={c("hero_headline")}
                className="text-4xl sm:text-5xl lg:text-7xl font-bold text-[#EDEDED] mb-6 leading-tight"
                delay={0.4}
              />

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="text-lg md:text-xl text-[#A3A3A3] mb-10 max-w-xl"
              >
                {c("hero_description")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/portfolio"
                  data-testid="hero-cta-portfolio"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  {c("hero_cta_primary")}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  {c("hero_cta_secondary")}
                </Link>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Scroll indicator with bounce */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-[#A3A3A3] text-xs uppercase tracking-widest">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-px h-12 bg-gradient-to-b from-[#A3A3A3] to-transparent"
            />
          </div>
        </motion.div>
      </section>

      {/* ═══ About Section ═══ */}
      <section data-testid="about-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="left">
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                {c("about_label")}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4 mb-6">
                {c("about_title")}
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">
                {c("about_p1")}
              </p>
              <p className="text-[#A3A3A3] leading-relaxed mb-8">
                {c("about_p2")}
              </p>
              <Link
                to="/about"
                data-testid="about-cta"
                className="inline-flex items-center gap-2 text-[#D4C5B0] font-medium hover:gap-4 transition-all"
              >
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </Reveal>

            <Reveal direction="right" delay={0.15}>
              <div className="relative">
                <ParallaxImage
                  src={resolveImageUrl(aboutImage)}
                  alt="Behind the scenes"
                  className="aspect-[4/5]"
                />
                <motion.div
                  initial={{ scaleX: 0, scaleY: 0 }}
                  whileInView={{ scaleX: 1, scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#D4C5B0] -z-10 origin-bottom-right"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ Services Section ═══ */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                What We Do
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4">
                Services
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Reveal key={service.title} delay={index * 0.12}>
                <Link
                  to={service.link}
                  data-testid={`service-card-${index}`}
                  className="service-card block p-8 h-full group"
                >
                  <service.icon className="w-10 h-10 text-[#D4C5B0] mb-6 transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="text-xl font-bold text-[#EDEDED] mb-4 group-hover:text-[#D4C5B0] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#A3A3A3] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#D4C5B0] text-sm font-medium">
                    Learn More
                    <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Featured Work ═══ */}
      <section data-testid="featured-work-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
              <div>
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                  Portfolio
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4">
                  Featured Work
                </h2>
              </div>
              <Link
                to="/portfolio"
                data-testid="view-all-work"
                className="inline-flex items-center gap-2 text-[#D4C5B0] font-medium hover:gap-4 transition-all"
              >
                View All Projects
                <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {portfolio.map((project, index) => (
              <Reveal key={project.id} delay={index * 0.1}>
                <Link
                  to={`/portfolio/${project.id}`}
                  data-testid={`portfolio-item-${index}`}
                  className="portfolio-item block aspect-[4/3] relative group"
                >
                  <img
                    src={resolveImageUrl(project.image_url)}
                    alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
                    className="w-full h-full object-cover"
                  />
                  {project.media_type === "video" && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                      <motion.div
                        whileHover={{ scale: 1.15 }}
                        className="w-16 h-16 bg-[#D4C5B0]/80 group-hover:bg-[#D4C5B0] rounded-full flex items-center justify-center transition-all"
                      >
                        <Play size={28} className="text-[#050505] ml-1" />
                      </motion.div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <span className="text-[#D4C5B0] text-xs uppercase tracking-widest mb-2 block">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-bold text-[#EDEDED]">
                      {project.title}
                    </h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Testimonials ═══ */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">
                Testimonials
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4">
                What Our Clients Say
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <Reveal key={testimonial.id} delay={index * 0.1}>
                <div
                  data-testid={`testimonial-${index}`}
                  className="testimonial-card p-8"
                >
                  <div className="quote-mark leading-none mb-4">"</div>
                  <p className="text-[#EDEDED] text-lg leading-relaxed mb-6 -mt-12">
                    {testimonial.content}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4C5B0] rounded-full flex items-center justify-center">
                      <span className="text-[#050505] font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-[#EDEDED] font-semibold">{testimonial.name}</p>
                      <p className="text-[#A3A3A3] text-sm">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Stats Section — Animated Counters ═══ */}
      <section data-testid="stats-section" className="py-24 bg-[#050505] border-y border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-8 md:gap-12">
            {[
              { number: "150", suffix: "+", label: "Projects Completed" },
              { number: "50", suffix: "+", label: "Happy Clients" },
              { number: "16", suffix: "", label: "Years Experience" }
            ].map((stat, index) => (
              <Reveal key={stat.label} delay={index * 0.1}>
                <div className="text-center">
                  <div className="stat-number text-4xl md:text-5xl lg:text-6xl mb-2">
                    <CountUp target={stat.number} suffix={stat.suffix} />
                  </div>
                  <p className="text-[#A3A3A3] text-sm uppercase tracking-wider">
                    {stat.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
